/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Button, useMediaQuery, CircularProgress, IconButton, Tooltip, Link, Checkbox } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import "./ProductList.css";
import { useAuth } from "../../context/authContext";
import { ASSETS_BASE_URL, RSPL_LIST } from "../../config";
import { BUTTON, LABELS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { saveRequestList } from "../../services/productCardService";
import { toast } from "../../context/ToastContext";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Delete } from "@mui/icons-material";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useOptions } from "../../context/optionContext";
import { buildRsplCodeToLabelMap, getRsplLabelForCode, parseRsplList } from "../../utilities/rspl";
import { formatMachineNumberFromRow } from "../../utilities/machineDisplay";

const ProductList = ({
	products,
	onAddToCart,
	onRemoveProduct,
	showDelete,
	showCheckbox = false,
	selectedProductIds = new Set(),
	onCheckedChange,
}) => {
	const { optionKeys } = useOptions();
	const resolvedRsplList = useMemo(
		() => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
		[optionKeys?.rsplList]
	);
	const rsplMapping = useMemo(
		() => buildRsplCodeToLabelMap(resolvedRsplList),
		[resolvedRsplList]
	);
	const { addToCart } = useCart();
	const { selectedSite, selectedSiteData } = useSite();
	const { stockImageUrl } = useBrandingAssets();
	const defaultImageSrc = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
	const [productImages, setProductImages] = useState({});
	const isMobile = useMediaQuery("(max-width:600px)");
	const [loadingStates, setLoadingStates] = useState({}); // Track loading states by product ID
	const [successStates, setSuccessStates] = useState({}); // Track success states by product ID
	const { user } = useAuth();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const triggerToast = (message, severity = "success") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};
	useEffect(() => {
		const updatedImages = {};
		for (const product of products) {
			const raw = product?.asset_data?.images?.[0];
			if (raw) {
				if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) {
					updatedImages[product.product_id] = raw;
				} else {
					const path = raw.startsWith("/") ? raw : `/${raw}`;
					updatedImages[product.product_id] = `${ASSETS_BASE_URL}${path}`;
				}
			} else if (product?.product_number) {
				updatedImages[product.product_id] = `${ASSETS_BASE_URL}/ProductImages/${product.product_number}.png`;
			} else {
				updatedImages[product.product_id] = defaultImageSrc;
			}
		}
		setProductImages(updatedImages);
	}, [products, defaultImageSrc]);



	const handleAddToRequest = async (product) => {
		setLoadingStates((prev) => ({ ...prev, [product.product_id]: true }));
		setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));
		setTimeout(() => {
			// Set success state
		}, 2000);

		const req_body = {
			machine_id: product.machine_id,
			product_id: product.product_id,
			site_number: selectedSite,
			site_id: selectedSiteData?.site_id,
			cus_id: selectedSiteData?.cus_id,
			user_id: user.user_id,
		};

		try {
			const response = await saveRequestList(req_body);
			if (response.status) {
				setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));

				setSuccessStates((prev) => ({ ...prev, [product.product_id]: true }));
				triggerToast(t('ALERT_MESSAGES.PRODUCT_ADDED_TO_REQUEST', { productName: product.product_name }), "success");
			} else if (!response.status) {
				triggerToast(t('ALERT_MESSAGES.PRODUCT_ALREADY_IN_REQUEST', { productName: product.product_name }), "warning");
			}

			setTimeout(() => {
				setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));
				setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));
			}, 2000);
		} catch (error) {
			console.error("Error fetching product name:", error);
		}

		// Reset the button text back to "Add to Cart" after 2 seconds
	};

	const handleAddToCart = async (product) => {
		// Start loading for this specific product
		setLoadingStates((prev) => ({ ...prev, [product.product_id]: true }));
		setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));

		try {
			// Call the addToCart function
			await addToCart({
				product_id: product.product_id,
				Quantity: 1,
				Site: selectedSite,
				site_id: selectedSiteData?.site_id,
				cus_id: selectedSiteData?.cus_id,
				machine_id: product.machine_id || null,
				site_line_id: product.site_line_id || null,
				line_name: product.line_name || "",
				line_number: product.line_number || "",
			});

			// Set success state for this product
			setSuccessStates((prev) => ({ ...prev, [product.product_id]: true }));
			triggerToast(t('ALERT_MESSAGES.PRODUCT_ADDED_TO_CART', { productNumber: product.product_number }), "success");

			// Reset states for this product after 2 seconds
			setTimeout(() => {
				setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));
				setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));

			}, 2000);
		} catch (error) {
			setSuccessStates((prev) => ({ ...prev, [product.product_id]: false }));
			triggerToast(`Error adding ${product.product_name} to Quote`, "error");
		} finally {
			setLoadingStates((prev) => ({ ...prev, [product.product_id]: false }));
		}
	};


	return (
		<>
			<Box width="100%">
				{products.map((product) => (
					<Box
						key={product.product_id}
						display="flex"
						flexDirection={isMobile ? "column" : "row"}
						justifyContent="space-between"
						alignItems="center"
						borderBottom="1px solid #ddd"
						padding="10px 0">
						<Box display="flex" alignItems="center">
							{showCheckbox && (
								<Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
									<Checkbox
										size="small"
										checked={selectedProductIds?.has?.(String(product.product_id)) || false}
										onClick={(e) => e.stopPropagation()}
										onChange={(e) => {
											e.stopPropagation();
											onCheckedChange?.(product, e.target.checked);
										}}
									/>
								</Box>
							)}
							<Box display="flex" justifyContent="flex-start" alignItems="center">
                            <img
                                src={productImages[product.product_id] || defaultImageSrc}
                                alt={product.product_name}
                                onError={(e)=>{ e.currentTarget.src = defaultImageSrc; }}
                                style={{ maxWidth: "60px", maxHeight: "60px" }}
                            />
							</Box>

							{/* Middle Column - Product Details */}
							<Box padding="0 20px" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
								<Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => {
									if (product?.product_id && product?.machine_id) {
										navigate(`/machines/${product?.machine_id}/${product?.product_id}${product?.site_line_id ? `?site_line_id=${encodeURIComponent(product.site_line_id)}` : ""}`)
									} else {
										Swal.fire({
											icon: "info",
											title: t('ALERT_MESSAGES.INFO'),
											text: "Product Link not found.",
										});
									}
								}}>

								<Typography variant="body1" fontWeight={"bold"}>{product.product_name}</Typography>
								</Link>
								<Typography fontSize={"11px"} variant="body2" color="textSecondary">
									<b>{t('UI_TEXT.PART')}</b> {product.product_number}
								</Typography>
									<Typography fontSize={"11px"} variant="body2" color="textSecondary">
										<b>{t('UI_TEXT.PART_TYPE')}</b>{" "}
										{getRsplLabelForCode(product.product_rspl_code, rsplMapping, t('UI_TEXT.OTHER'))}
									</Typography>
									{product.machine_number && (
										<Typography fontSize={"11px"} variant="body2" color="textSecondary">
											<b>{t('UI_TEXT.MACHINE')}</b> {formatMachineNumberFromRow(product)}
										</Typography>
									)}

								</Box>
						</Box>
						{/* Right Column - Add to Cart and Delete Buttons */}
						<Box
							sx={{ width: isMobile ? "100%" : "auto" }}
							display="flex"
							justifyContent={isMobile ? "space-between" : "center"}
							marginTop={isMobile ? "10px" : "0px"}
							alignItems="center"
							gap="4px"
							paddingRight="4px">
							<>
							{/* <Typography variant="h6" mr={1.5} color="primary">
								{formatCurrency(
									product?.[`price_${selectedSiteCurrency}`] ?? product?.product_sell_price ?? 0,
									selectedSiteCurrency
								)}
							</Typography> */}

								<Button
									size="small"
									variant="contained"
									sx={{
										backgroundColor: "var(--primary-main)",
										padding: "8px 15px",
										fontWeight: "bolder",
									}}
									onClick={(event) => {
										event.stopPropagation();
										handleAddToCart(product);
									}}
									disabled={loadingStates[product.product_id]} // Disable the button while loading or after success
								>
									{loadingStates[product.product_id] ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />
											{t('BUTTON.ADDING')}
										</>
									) : successStates[product.product_id] ? (
										<>
											<CheckCircleIcon sx={{ color: "white", marginRight: 1 }} />
											{t('BUTTON.ADDED')}
										</>
									) : (
										t('LABELS.ADD_TO_CART')
									)}
								</Button>
								</>
							{showDelete && (
								<Tooltip title={t("UI_TEXT.TOOLTIP_REMOVE_FROM_LIST")}>
									<IconButton
									onClick={() => onRemoveProduct(product.product_id, product.machine_id)}
										color="error"
								>
										<Delete />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					</Box>
				))}
			</Box>
		</>
	);
};

export default ProductList;
